import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import StreamSurface, { type StreamLicence } from "../../player/StreamSurface";
import { ApiError, portalApi } from "../api";
import type { TitleDetail } from "../types";
import {
  drmSupported,
  type StreamFailure,
} from "@shared/player/streamPlayback";
import type {
  PlaybackProgress,
  StreamControls,
  StreamTracks,
} from "@shared/player/controls";
import type { TrackOption } from "@shared/player/tracks";
import {
  licenceUrlsOf,
  type WebPlayFormat,
  type WebPlayResponse,
} from "@shared/consumer-tvod";
import { ArrowLeft } from "../icons";
import { useI18n, type MessageKey } from "../i18n";
import { entryState } from "../routes";
import { usePortal } from "../state";
import { useScrubThumbnail } from "../../player/useScrubThumbnail";

/**
 * The web player (PLAY-3 step B): the SAME engine as the Foyer TV
 * (`src/player/StreamSurface`, PLAY-1) behind the portal's play gate.
 *
 * - **The gate decides, the browser asks.** `POST /api/interactiveplayer/v1/portal/play/:id` checks
 *   the rental, starts the first-play clock and opens the member's one play
 *   session; anything it refuses is shown as a sentence, never played.
 * - **Capability, never platform.** A protected title plays only when this
 *   browser answers `requestMediaKeySystemAccess` for a key system the stream
 *   offers. Safari without FairPlay (PLAY-6) gets "not in this browser yet" -
 *   it never falls back to the clear.
 * - **Licence renewal is a request filter**: every licence request the CDM
 *   makes gets a freshly minted URL from `/play/:session/licence`.
 * - **The heartbeat is obeyed**: anything but `ok` stops the picture
 *   (`taken_over` = the same member started another stream).
 */

type Phase =
  | { name: "starting" }
  | { name: "refused"; message: MessageKey }
  | { name: "playing"; play: WebPlayResponse }
  | { name: "stopped"; message: MessageKey };

const DEVICE_KEY = "foyer-play-device";

/**
 * Does this browser stream through Apple's ManagedMediaSource? A capability
 * (the object exists), never a user-agent sniff - and precisely the one that
 * failed: Safari's MMS refused Shaka's HLS appends, Chrome has no MMS.
 */
export function hasManagedMediaSource(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as unknown as { ManagedMediaSource?: unknown })
      .ManagedMediaSource !== "undefined"
  );
}

/**
 * Which of the offered renditions THIS browser plays, and how.
 *
 * Measured 2026-10-05 (Safari 26.1, Chrome 145, both "maybe" for HLS):
 * - Shaka feeding Safari's ManagedMediaSource fails - TS-HLS refused an
 *   audio append (3024), DASH stuck looping 0-2 s - while Safari's OWN HLS
 *   player plays and seeks;
 * - Chrome's own HLS player breaks on seek, while DASH through Shaka is clean.
 *
 * So: a ManagedMediaSource browser that can play HLS itself gets the clear
 * HLS rendition, natively; everyone else gets DASH through Shaka. A protected
 * title is only offered as DASH, so Safari still meets the honest "not in
 * this browser yet" until FairPlay (PLAY-6).
 */
export function chooseFormat(
  formats: WebPlayFormat[],
  env: { managedMediaSource: boolean; nativeHls: boolean }
): { format: WebPlayFormat; native: boolean } | null {
  if (formats.length === 0) return null;
  if (env.managedMediaSource && env.nativeHls) {
    const hls = formats.find((f) => f.format === "HLS" && !f.drm);
    if (hls) return { format: hls, native: true };
  }
  const dash = formats.find((f) => f.format === "DASH");
  return { format: dash ?? formats[0]!, native: false };
}

function browserEnv(): { managedMediaSource: boolean; nativeHls: boolean } {
  let nativeHls = false;
  try {
    nativeHls =
      document
        .createElement("video")
        .canPlayType("application/vnd.apple.mpegurl") !== "";
  } catch {
    nativeHls = false;
  }
  return { managedMediaSource: hasManagedMediaSource(), nativeHls };
}
const HIDE_CONTROLS_MS = 3000;

/** A stable id for this browser; a fresh one if storage is unavailable. */
function deviceId(): string {
  const make = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  try {
    const stored = window.localStorage.getItem(DEVICE_KEY);
    if (stored) return stored;
    const id = make();
    window.localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return make();
  }
}

const REFUSAL: Record<string, MessageKey> = {
  not_rented: "watch.locked",
  purchaser_cannot_play: "watch.purchaser",
  expired: "watch.expired",
  revoked: "watch.revoked",
  not_found: "watch.missing",
  rate_limited: "watch.busy",
  unavailable: "watch.unavailable",
  session_expired: "watch.signedOut",
  session_revoked: "watch.signedOut",
};

const STOPPED: Record<string, MessageKey> = {
  taken_over: "watch.takenOver",
  expired: "watch.expired",
  revoked: "watch.revoked",
  logged_out: "watch.signedOut",
  ended: "watch.ended",
};

const FAILURE: Record<StreamFailure, MessageKey> = {
  licence_refused: "watch.fail.licence",
  licence_expired: "watch.fail.licenceExpired",
  manifest_unreachable: "watch.fail.network",
  codec_unsupported: "watch.fail.codec",
  decoder_exhausted: "watch.fail.unknown",
  network_stalled: "watch.fail.network",
  unknown: "watch.fail.unknown",
};

export default function Watch(props: { assetId: string }) {
  const { t, lang } = useI18n();
  const { replace } = usePortal();
  const [phase, setPhase] = useState<Phase>({ name: "starting" });
  const [detail, setDetail] = useState<TitleDetail | null>(null);
  const [failure, setFailure] = useState<StreamFailure | null>(null);
  const [failureCode, setFailureCode] = useState("");
  const [progress, setProgress] = useState<PlaybackProgress | null>(null);
  const [tracks, setTracks] = useState<StreamTracks | null>(null);
  const [menu, setMenu] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [idle, setIdle] = useState(false);
  const [ended, setEnded] = useState(false);
  const controls = useRef<StreamControls | null>(null);
  // The same handle as state, for the scrub preview's hook.
  const [handle, setHandle] = useState<StreamControls | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const position = useRef(0);
  const sessionId = useRef<string | null>(null);

  // What is playing, for the title bar (shared with the title page's cache).
  useEffect(() => {
    portalApi
      .title(props.assetId, lang)
      .then(setDetail)
      .catch(() => undefined);
  }, [props.assetId, lang]);

  /* ── open the play session ── */
  useEffect(() => {
    let cancelled = false;
    setPhase({ name: "starting" });
    (async () => {
      try {
        const play = await portalApi.play(props.assetId, deviceId());
        if (cancelled) {
          portalApi.stop(play.playSessionId);
          return;
        }
        const format = chooseFormat(play.formats, browserEnv())?.format;
        if (!format) {
          setPhase({ name: "refused", message: "watch.unavailable" });
          return;
        }
        if (format.drm) {
          const offered = Object.keys(format.drm);
          const answers = await Promise.all(
            offered.map((k) => drmSupported(k))
          );
          if (!answers.some(Boolean)) {
            // Fail closed: a protected title is never played in the clear.
            portalApi.stop(play.playSessionId);
            if (!cancelled) {
              setPhase({
                name: "refused",
                message: "watch.browserUnsupported",
              });
            }
            return;
          }
        }
        if (cancelled) {
          portalApi.stop(play.playSessionId);
          return;
        }
        sessionId.current = play.playSessionId;
        setPhase({ name: "playing", play });
      } catch (e) {
        if (cancelled) return;
        const code = e instanceof ApiError ? e.code : "";
        setPhase({
          name: "refused",
          message: REFUSAL[code] ?? "watch.unavailable",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.assetId]);

  const playing = phase.name === "playing" ? phase.play : null;

  /* ── stop: leaving the page, closing the tab ── */
  useEffect(() => {
    if (!playing) return;
    const id = playing.playSessionId;
    const stop = () => portalApi.stop(id, position.current);
    window.addEventListener("pagehide", stop);
    return () => {
      window.removeEventListener("pagehide", stop);
      stop();
      if (sessionId.current === id) sessionId.current = null;
    };
  }, [playing]);

  /* ── heartbeat: anything but ok stops the picture ── */
  useEffect(() => {
    if (!playing) return;
    const id = playing.playSessionId;
    const timer = window.setInterval(
      () => {
        portalApi
          .heartbeat(id, Math.floor(position.current))
          .then((answer) => {
            if (answer.status === "ok") return;
            controls.current?.pause();
            setPhase({
              name: "stopped",
              message: STOPPED[answer.status] ?? "watch.ended",
            });
          })
          .catch((e: unknown) => {
            if (e instanceof ApiError && e.status === 401) {
              controls.current?.pause();
              setPhase({ name: "stopped", message: "watch.signedOut" });
            }
            // A network blip is not a verdict: the next beat tries again.
          });
      },
      Math.max(15, playing.heartbeatSeconds) * 1000
    );
    return () => window.clearInterval(timer);
  }, [playing]);

  /* ── licence renewal: a fresh URL on every licence request ── */
  const mintLicence = useCallback(async (): Promise<StreamLicence | null> => {
    const id = sessionId.current;
    if (!id) return null;
    try {
      const fresh = await portalApi.licence(id);
      return { widevineUrl: fresh.widevine, playreadyUrl: fresh.playready };
    } catch {
      return null;
    }
  }, []);

  const chosen = playing ? chooseFormat(playing.formats, browserEnv()) : null;
  const format = chosen?.format;
  const initialLicence = format?.drm
    ? (() => {
        const urls = licenceUrlsOf(format);
        return { widevineUrl: urls.widevine, playreadyUrl: urls.playready };
      })()
    : null;

  /* ── controls ── */
  const video = () =>
    stage.current?.querySelector("video") as HTMLVideoElement | null;

  const toggle = useCallback(() => controls.current?.togglePlay(), []);
  const skip = useCallback((delta: number) => {
    const p = controls.current?.progress();
    if (p) controls.current?.seekTo(p.currentTime + delta);
  }, []);
  const fullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void stage.current?.requestFullscreen?.();
  }, []);
  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  useEffect(() => {
    const v = video();
    if (v) v.volume = volume;
  }, [volume, playing]);

  // Keyboard: the shortcuts every streaming site has taught people.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, button")) {
        if (e.key !== " " || target.tagName !== "BUTTON") return;
      }
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          toggle();
          break;
        case "ArrowLeft":
          e.preventDefault();
          skip(-10);
          break;
        case "ArrowRight":
          e.preventDefault();
          skip(10);
          break;
        case "f":
          fullscreen();
          break;
        case "m":
          toggleMute();
          break;
        default:
          return;
      }
      wake();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Controls fade out while watching, back on any movement.
  const idleTimer = useRef<number | null>(null);
  const wake = () => {
    setIdle(false);
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(
      () => setIdle(true),
      HIDE_CONTROLS_MS
    );
  };
  useEffect(
    () => () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    },
    []
  );

  const title = detail?.title.title ?? "";
  const back = `/titles/${encodeURIComponent(props.assetId)}`;
  // Back is ALWAYS the title page (the asset card, Sigge 2026-10-05): popped
  // when we came from it, so history never grows a second title entry that
  // a later "back" would step into the player through; replaced otherwise
  // (opened from Mitt innehåll, or a shared /watch link).
  const toTitle = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (entryState().from === back) window.history.back();
    else replace(back);
  };

  if (phase.name !== "playing" || !format) {
    const message =
      phase.name === "refused" || phase.name === "stopped"
        ? phase.message
        : null;
    return (
      <div className="player player--still">
        <div className="player__top">
          <a
            href={back}
            className="player__back"
            aria-label={t("common.back")}
            onClick={toTitle}
          >
            <ArrowLeft className="player__back-icon" />
          </a>
          <span className="player__name">{title}</span>
        </div>
        <div className="player__notice" role={message ? "alert" : "status"}>
          {message ? (
            <>
              <p>{t(message)}</p>
              <a href={back} className="btn btn--ghost" onClick={toTitle}>
                {t("common.back")}
              </a>
            </>
          ) : (
            <span
              className="player__spinner"
              aria-label={t("watch.starting")}
            />
          )}
        </div>
      </div>
    );
  }

  const p = progress;
  const duration = p && Number.isFinite(p.duration) ? p.duration : 0;
  const current = p?.currentTime ?? 0;

  return (
    <div
      ref={stage}
      className={`player${idle && !p?.paused ? " player--idle" : ""}`}
      onPointerMove={wake}
      onClick={(e) => {
        if (
          e.target === e.currentTarget ||
          (e.target as HTMLElement).tagName === "VIDEO"
        ) {
          toggle();
        }
      }}
    >
      <StreamSurface
        url={format.mediaLocator}
        licence={initialLicence}
        mintLicence={format.drm ? mintLicence : undefined}
        muted={muted}
        locale={lang}
        showFailure={false}
        // See chooseFormat: Safari plays the HLS rendition with its own
        // player; everyone else plays DASH through Shaka.
        preferNativeHls={chosen?.native === true}
        onReady={(c) => {
          controls.current = c;
          setHandle(c);
          setTracks(c.tracks());
        }}
        onTracks={setTracks}
        onProgress={(next) => {
          position.current = next.currentTime;
          setProgress(next);
          // Seeking back from the end plays again: "Slut." goes away.
          if (
            Number.isFinite(next.duration) &&
            next.duration > 0 &&
            next.currentTime < next.duration - 0.5
          ) {
            setEnded(false);
          }
        }}
        onFailed={(f, detail) => {
          // Logged and shown as a code: a sentence alone told us nothing the
          // first time Safari failed (2026-10-05).
          console.warn("[play] playback failed:", f, detail);
          setFailure(f);
          setFailureCode(detail);
        }}
        onEnded={() => setEnded(true)}
      />

      <div className="player__top">
        <a
          href={back}
          className="player__back"
          aria-label={t("common.back")}
          onClick={toTitle}
        >
          <ArrowLeft className="player__back-icon" />
        </a>
        <span className="player__name">{title}</span>
      </div>

      {failure ? (
        <div className="player__notice" role="alert">
          <p>{t(FAILURE[failure])}</p>
          {failureCode ? (
            <small className="player__code">
              {t("watch.code")}: {failureCode}
            </small>
          ) : null}
          <a href={back} className="btn btn--ghost" onClick={toTitle}>
            {t("common.back")}
          </a>
        </div>
      ) : ended ? (
        <div className="player__notice" role="status">
          <p>{t("watch.finished")}</p>
          <a href={back} className="btn btn--ghost" onClick={toTitle}>
            {t("common.back")}
          </a>
        </div>
      ) : null}

      <div className="player__bar">
        <Timeline
          current={current}
          duration={duration}
          controls={handle}
          onSeek={(t) => controls.current?.seekTo(t)}
          label={t("watch.seek")}
        />
        <div className="player__buttons">
          <button
            type="button"
            onClick={toggle}
            aria-label={p?.paused ? t("watch.play") : t("watch.pause")}
          >
            {p?.paused ? <PlayGlyph /> : <PauseGlyph />}
          </button>
          <button
            type="button"
            onClick={() => skip(-10)}
            aria-label={t("watch.back10")}
          >
            −10
          </button>
          <button
            type="button"
            onClick={() => skip(10)}
            aria-label={t("watch.fwd10")}
          >
            +10
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? t("watch.unmute") : t("watch.mute")}
          >
            <SpeakerGlyph off={muted || volume === 0} />
          </button>
          <input
            type="range"
            className="player__volume"
            min={0}
            max={1}
            step={0.05}
            value={muted ? 0 : volume}
            aria-label={t("watch.volume")}
            onChange={(e) => {
              setVolume(Number(e.target.value));
              setMuted(Number(e.target.value) === 0);
            }}
          />
          <span className="player__spacer" />
          {tracks && (tracks.audio.length > 1 || tracks.text.length > 0) ? (
            <div className="player__menu-anchor">
              <button
                type="button"
                aria-expanded={menu}
                aria-label={t("watch.tracks")}
                onClick={() => setMenu((m) => !m)}
              >
                <SubtitleGlyph />
              </button>
              {menu ? (
                <TrackMenu
                  tracks={tracks}
                  onAudio={(o) => controls.current?.selectAudio(o)}
                  onText={(o) => controls.current?.selectText(o)}
                  onClose={() => setMenu(false)}
                />
              ) : null}
            </div>
          ) : null}
          {"pictureInPictureEnabled" in document &&
          (document as Document & { pictureInPictureEnabled?: boolean })
            .pictureInPictureEnabled ? (
            <button
              type="button"
              aria-label={t("watch.pip")}
              onClick={() => {
                const v = video() as
                  | (HTMLVideoElement & {
                      requestPictureInPicture?: () => Promise<unknown>;
                    })
                  | null;
                void v?.requestPictureInPicture?.().catch(() => undefined);
              }}
            >
              <PipGlyph />
            </button>
          ) : null}
          <button
            type="button"
            onClick={fullscreen}
            aria-label={t("watch.fullscreen")}
          >
            <FullscreenGlyph />
          </button>
        </div>
      </div>
    </div>
  );
}

function TrackMenu(props: {
  tracks: StreamTracks;
  onAudio: (o: TrackOption) => void;
  onText: (o: TrackOption | null) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { audio, text, textVisible } = props.tracks;
  return (
    <div className="player__menu" role="dialog" aria-label={t("watch.tracks")}>
      {audio.length > 1 ? (
        <fieldset>
          <legend>{t("title.audio")}</legend>
          {audio.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={o.active}
              onClick={() => {
                props.onAudio(o);
                props.onClose();
              }}
            >
              {o.label}
              {o.note ? <small> · {o.note}</small> : null}
            </button>
          ))}
        </fieldset>
      ) : null}
      {text.length > 0 ? (
        <fieldset>
          <legend>{t("title.subtitles")}</legend>
          <button
            type="button"
            aria-pressed={!textVisible}
            onClick={() => {
              props.onText(null);
              props.onClose();
            }}
          >
            {t("watch.subtitlesOff")}
          </button>
          {text.map((o) => (
            <button
              key={o.value}
              type="button"
              aria-pressed={textVisible && o.active}
              onClick={() => {
                props.onText(o);
                props.onClose();
              }}
            >
              {o.label}
              {o.note ? <small> · {o.note}</small> : null}
            </button>
          ))}
        </fieldset>
      ) : null}
    </div>
  );
}

/**
 * The timeline: our own element, not `<input type="range">`.
 *
 * A native range input dispatches pointer events differently per browser
 * while its thumb is dragged (Sigge, 2026-10-05: "I am not allowed to
 * scrub"). This one captures the pointer, so press, drag and release arrive
 * here in every browser:
 *
 * - hover shows the preview (time + frame) and does not seek;
 * - press + drag moves the playhead marker and the preview;
 * - release seeks ONCE, where the pointer let go;
 * - a plain click seeks to the clicked point.
 *
 * Keyboard: it is focusable with slider semantics; the arrow keys are the
 * player's own shortcuts (±10 s), handled once at the window.
 */
function Timeline(props: {
  current: number;
  duration: number;
  controls: StreamControls | null;
  onSeek: (seconds: number) => void;
  label: string;
}) {
  const bar = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  // The press, read synchronously: a quick click delivers pointerup before
  // React has re-rendered with the pointerdown's state, and a release that
  // read the stale `drag === null` would drop the seek (found on dev
  // 2026-10-05 by dispatching down+up back to back).
  const pressed = useRef<number | null>(null);
  const { duration } = props;

  const timeAt = (clientX: number): number | null => {
    const el = bar.current;
    if (!el || !duration || !Number.isFinite(clientX)) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return frac * duration;
  };

  const shown = drag ?? props.current;
  const played = duration ? Math.min(1, shown / duration) * 100 : 0;
  const preview = drag ?? hover;

  return (
    <div className="player__timeline">
      <span>{clock(shown)}</span>
      <div
        ref={bar}
        className={`player__track${drag !== null ? " player__track--drag" : ""}`}
        role="slider"
        tabIndex={0}
        aria-label={props.label}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(shown)}
        aria-valuetext={clock(shown)}
        onPointerDown={(e) => {
          const t = timeAt(e.clientX);
          if (t === null) return;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            /* a synthetic or already-released pointer: drag still works */
          }
          pressed.current = t;
          setDrag(t);
          setHover(t);
        }}
        onPointerMove={(e) => {
          const t = timeAt(e.clientX);
          if (t === null) return;
          setHover(t);
          if (pressed.current !== null) {
            pressed.current = t;
            setDrag(t);
          }
        }}
        onPointerUp={(e) => {
          const down = pressed.current;
          if (down === null) return;
          pressed.current = null;
          const t = timeAt(e.clientX) ?? down;
          setDrag(null);
          props.onSeek(t);
        }}
        onPointerCancel={() => {
          pressed.current = null;
          setDrag(null);
        }}
        onPointerLeave={() => {
          if (pressed.current === null) setHover(null);
        }}
      >
        <div className="player__rail">
          <div className="player__played" style={{ width: `${played}%` }} />
          <div className="player__knob" style={{ left: `${played}%` }} />
        </div>
        {preview !== null && duration ? (
          <ScrubPreview
            controls={props.controls}
            seconds={preview}
            leftPercent={(preview / duration) * 100}
          />
        ) : null}
      </div>
      <span>
        {duration ? `−${clock(Math.max(0, duration - shown))}` : "--:--"}
      </span>
    </div>
  );
}

/**
 * The frame and the time under the pointer on the timeline.
 *
 * The picture comes from the stream's own thumbnail track when it has one
 * (Ålcom's DASH carries `thumbnails-240x135` sprite sheets), fetched only once
 * the pointer settles (`useScrubThumbnail`, shared with the TV). Without a
 * track - a stream that has none, or Safari playing HLS natively, where the
 * manifest is not parsed by us - the bubble shows the time alone.
 */
function ScrubPreview(props: {
  controls: StreamControls | null;
  seconds: number;
  leftPercent: number;
}) {
  // Two-second buckets: a pointer moving across the bar does not ask for a
  // new sheet at every pixel.
  const target = Math.round(props.seconds / 2) * 2;
  const thumb = useScrubThumbnail(props.controls, true, target, 120);
  const scale = thumb && thumb.width > 0 ? Math.min(1, 240 / thumb.width) : 1;
  return (
    <div
      className="player__preview"
      style={{
        left: `clamp(130px, ${props.leftPercent}%, calc(100% - 130px))`,
      }}
      aria-hidden="true"
    >
      {thumb && thumb.width > 0 && thumb.height > 0 ? (
        <div
          className="player__thumb"
          style={{ width: thumb.width * scale, height: thumb.height * scale }}
        >
          <div
            style={{
              width: thumb.width,
              height: thumb.height,
              backgroundImage: cssUrl(thumb.url),
              backgroundPosition: thumb.sprite
                ? `-${thumb.x}px -${thumb.y}px`
                : "center",
              backgroundSize: thumb.sprite
                ? thumb.sheetWidth && thumb.sheetHeight
                  ? `${thumb.sheetWidth}px ${thumb.sheetHeight}px`
                  : undefined
                : "cover",
              backgroundRepeat: "no-repeat",
              transform: `scale(${scale})`,
              transformOrigin: "0 0",
            }}
          />
        </div>
      ) : null}
      <span className="player__preview-time">{clock(props.seconds)}</span>
    </div>
  );
}

/**
 * A quoted CSS url(). Unquoted, a "(" or a space in the address voids the
 * whole declaration silently and the element paints nothing (the
 * tv-shell-and-home pitfall).
 */
function cssUrl(url: string): string {
  return `url("${url.replace(/["\\\n]/g, (c) => `\\${c}`)}")`;
}

function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

const glyph = (children: ReactNode, fill = false) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill={fill ? "currentColor" : "none"}
    stroke={fill ? "none" : "currentColor"}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);
const PlayGlyph = () => glyph(<path d="M7 4l13 8-13 8z" />, true);
const PauseGlyph = () =>
  glyph(
    <>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </>,
    true
  );
const SpeakerGlyph = (p: { off: boolean }) =>
  glyph(
    <>
      <path d="M4 9h4l5-4v14l-5-4H4z" />
      {p.off ? (
        <path d="M17 9l4 6M21 9l-4 6" />
      ) : (
        <path d="M17 9a4 4 0 0 1 0 6" />
      )}
    </>
  );
const SubtitleGlyph = () =>
  glyph(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 13h4M13 13h4M7 16h7" />
    </>
  );
const PipGlyph = () =>
  glyph(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <rect x="12" y="11" width="7" height="6" rx="1" />
    </>
  );
const FullscreenGlyph = () =>
  glyph(<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />);
